import React, { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { MoreOutlined as MoreVertIcon } from '@ant-design/icons';
import { Button, Dropdown, Spin, Table } from 'antd';
import type { MenuProps } from 'antd';

import Alert from 'components/Alert';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import formatDocument from 'v2/components/Shared/Masks/document';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import whitelistService from 'v2/services/whitelist';

const List = () => {
  const [isLoading] = useObservable(() => whitelistService.isLoading(), []);
  const [whitelists] = useObservable(() => whitelistService.getWhiteLists(), []);
  const [pagination] = useObservable(() => whitelistService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (_current: number, perPage: number) => {
      whitelistService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      whitelistService.setPagination({ ...pagination, current_page: page });
    },
    [pagination]
  );

  const openDialog = useCallback((whitelist: any) => {
    whitelistService.setCurrentWhiteList(whitelist);
    whitelistService.setCreateAndEditDialog(true);
  }, []);

  const [deleteWhitelist] = useCallbackObservable((whitelist: any) => {
    return whitelistService.delete(whitelist).pipe(
      tap(() => toast.success('Cliente removido com sucesso.')),
      catchError(() => {
        toast.error('Ocorreu um erro ao tentar remover cliente');
        return of(true);
      })
    );
  }, []);

  const onDelete = useCallback(
    async (whitelist: any) => {
      const confirm = await Alert.show({
        message: (
          <>
            <p className='mt-4! mb-0!'>
              <b>Nome:</b> {whitelist?.customer_name}
            </p>
            <p className='mb-0!'>
              <b>E-mail:</b> {whitelist.customer_email}
            </p>
            <p className='mb-0!'>
              <b>CPF/CNPJ:</b> {formatDocument.apply(whitelist.customer_document)}
            </p>
          </>
        ),
        buttonOk: 'Excluir',
        title: 'Realmente deseja excluir da whitelist o cliente abaixo?',
        confirmation: true
      });

      if (!confirm) return;
      deleteWhitelist(whitelist);
    },
    [deleteWhitelist]
  );

  const editPermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:editar');
  const deletePermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:excluir');

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
    { title: 'Nome', dataIndex: 'customer_name', key: 'customer_name', width: 200 },
    { title: 'E-mail', dataIndex: 'customer_email', key: 'customer_email', width: 250 },
    {
      title: 'CPF/CNPJ',
      dataIndex: 'customer_document',
      key: 'customer_document',
      width: 200,
      render: (doc: string) => formatDocument.apply(doc)
    },
    ...(editPermission || deletePermission
      ? [
          {
            title: 'Ações',
            key: 'actions',
            width: 50,
            fixed: 'right' as const,
            align: 'right' as const,
            render: (_: any, record: any) => {
              const items: MenuProps['items'] = [];

              if (editPermission) {
                items.push({ key: 'edit', label: 'Editar', onClick: () => openDialog(record) });
              }

              if (deletePermission) {
                items.push({ key: 'delete', label: 'Excluir', danger: true, onClick: () => onDelete(record) });
              }

              return (
                <Dropdown menu={{ items }} placement='bottomRight' trigger={['click']}>
                  <Button type='text' icon={<MoreVertIcon />} aria-label='Ações' />
                </Dropdown>
              );
            }
          }
        ]
      : [])
  ];

  return (
    <Spin spinning={!!isLoading}>
      <Table
        id='whitelist_table'
        dataSource={whitelists || []}
        columns={columns}
        rowKey='id'
        scroll={{ x: 800 }}
        pagination={{
          current: pagination?.current_page,
          pageSize: pagination?.per_page,
          total: pagination?.total_items || 0,
          onChange: onChangePage,
          onShowSizeChange: onChangePerPage,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`
        }}
      />
    </Spin>
  );
};

export default memo(List);
